import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import socket from "../socket/socket";
import PendingOrders from "./ongoing-orders/PendingOrders";
import WaitingOrders from "./ongoing-orders/WaitingOrders";
import ArrivedOrders from "./ongoing-orders/ArrivedOrders";
import ScannedOrders from "./ongoing-orders/ScannedOrders";
import EnRouteOrders from "./ongoing-orders/EnRouteOrders";
import PermissionGate from "../layout/PermissionGate";
import StatusTooltipWrapper from "./ongoing-orders/StatusTooltipWrapper";
import DelayedOrderEvents from "./DelayedOrderEvents";
import FailedOrders from "./ongoing-orders/FailedOrders";
import ScheduledOrders from "./ongoing-orders/ScheduledOrders";

const OngoingOrders = () => {
  const [data, setData] = useState([]);
  const [stat, setStat] = useState({});
  const [sort, setSort] = useState(false);
  const sortRef = useRef(sort);
  const token = localStorage.getItem("token");

  const fetchOrders = () => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}order/ongoing-orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setData(res.data.data);
        }
      })
      .catch(console.error);
  };

  const fetchOrder = (branchId, status = "") => {
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT}order/ongoing-order/${branchId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          const newBranch = res.data.data;

          setData((prev) => {
            const prevList = prev?.filter(
              (b) => String(b._id) !== String(branchId),
            );

            if (!["0", "1", "2", "3", "4", "5", "scheduled"].includes(status))
              fetchOrderStat();

            if (!newBranch || Object.keys(newBranch).length === 0)
              return prevList;

            const finalList = [...prevList, newBranch];
            return sortRef.current
              ? sortBranchesByOldestOrder(finalList)
              : sortBranchesByLatestOrder(finalList);
          });
        }
      })
      .catch(console.error);
  };

  const fetchOrderStat = () => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}order/ongoing-order-stat`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (
          res.data.code === 1 &&
          Array.isArray(res?.data?.data) &&
          res?.data?.data?.length > 0
        ) {
          setStat(res?.data?.data?.[0]);
        }
      })
      .catch(console.error);
  };

  const getCount = (branch, status) =>
    branch?.statusGroups?.filter((g) => g?.status === status)?.at(-1)?.count ??
    0;

  const compareByPriority = (a, b, latestFirst) => {
    const statuses = ["0", "1", "2", "3", "4", "5"];
    for (const s of statuses) {
      const diff = getCount(b, s) - getCount(a, s);
      if (diff !== 0) return diff;
    }
    const aDate = new Date(a.latestUpdatedAt);
    const bDate = new Date(b.latestUpdatedAt);
    return latestFirst ? bDate - aDate : aDate - bDate;
  };

  const sortBranchesByLatestOrder = (data) =>
    [...data].sort((a, b) => compareByPriority(a, b, true));

  const sortBranchesByOldestOrder = (data) =>
    [...data].sort((a, b) => compareByPriority(a, b, false));

  useEffect(() => {
    sortRef.current = sort;
    if (sort === true) {
      const sorted = sortBranchesByOldestOrder(data);
      setData(sorted);
    } else {
      const sorted = sortBranchesByLatestOrder(data);
      setData(sorted);
    }
  }, [sort]);

  useEffect(() => {
    fetchOrders();
    fetchOrderStat();

    socket.on(`admin:ordersUpdate`, (data) => {
      if (data && data?.branchId) {
        fetchOrder(data?.branchId, data?.status);
        fetchOrderStat();
      }
    });

    return () => {
      socket.off(`admin:ordersUpdate`);
    };
  }, []);

  return (
    <div className="d-flex flex-column flex-root">
      <div className="page d-flex flex-row flex-column-fluid">
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <div
              id="kt_content_container"
              className="d-flex flex-column-fluid align-items-start container-xxl"
            >
              <div className="content flex-row-fluid" id="kt_content">
                <div className="row align-items-stretch mb-3">
                  <PermissionGate required={`view_ongoing_orders`}>
                    <div className="col-md-6">
                      <div className="card mb-5 h-100">
                        <div className="card-header d-flex justify-content-between">
                          <div className="card-title">Ongoing Orders</div>
                          <div className="card-title">
                            {data?.length > 0 && (
                              <button
                                onClick={() => {
                                  setSort((prev) => !prev);
                                }}
                                title="Filter"
                                className={`btn btn-sm ${
                                  sort === true ? "btn-light" : "btn-primary"
                                } ps-3 pe-2`}
                              >
                                <i className="ki-duotone ki-sort fs-1">
                                  <span className="path1"></span>
                                  <span className="path2"></span>
                                  <span className="path3"></span>
                                  <span className="path4"></span>
                                </i>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="card-body py-4" id="filterDataWrap">
                          {data?.length === 0 ? (
                            <div className="text-center w-100">
                              <img
                                className="w-25 mx-auto py-20"
                                src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                alt="no-content"
                              />
                            </div>
                          ) : (
                            <div className="w-100">
                              {data &&
                                data?.map((item, index) => {
                                  return (
                                    item?.branchName && (
                                      <div
                                        key={index}
                                        className="d-flex justify-content-between mb-3"
                                      >
                                        <div className="py-2">
                                          {item?.branchName}
                                        </div>
                                        <StatusTooltipWrapper item={item}>
                                          <div>
                                            <PendingOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                      "0",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"0"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                          <div>
                                            <WaitingOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                      "1",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"1"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                          <div>
                                            <ArrivedOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                      "2",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"2"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                          <div>
                                            <ScannedOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                        "3" ||
                                                      statusGroup?.status ===
                                                        "4",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"4"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                          <div>
                                            <EnRouteOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                      "5",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"5"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                          <div>
                                            <FailedOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                      "-1",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"-1"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                          <div>
                                            <ScheduledOrders
                                              count={
                                                item?.statusGroups
                                                  ?.filter(
                                                    (statusGroup) =>
                                                      statusGroup?.status ===
                                                      "scheduled",
                                                  )
                                                  ?.at(-1)?.count ?? 0
                                              }
                                              status={"scheduled"}
                                              branchId={item?.branchId}
                                            />
                                          </div>
                                        </StatusTooltipWrapper>
                                      </div>
                                    )
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </PermissionGate>

                  <PermissionGate required={`view_delayed_order_event`}>
                    <div className="col-md-6">
                      <DelayedOrderEvents />
                    </div>
                  </PermissionGate>
                </div>

                <PermissionGate required={`view_daily_order_stat`}>
                  <div className="card mb-5">
                    <div className="card-header">
                      <div className="card-title">Daily Order Stat</div>
                    </div>

                    <div className="card-body py-2">
                      <div className="row">
                        <div className="col-md-4">
                          <div className="w-100 h6 bg-success text-white p-5 d-flex justify-content-between rounded">
                            <div>Completed Orders</div>
                            <div>{stat?.success ?? 0}</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="w-100 h6 bg-warning text-white p-5 d-flex justify-content-between rounded">
                            <div>Cancelled Orders</div>
                            <div>{stat?.cancelled ?? 0}</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="w-100 h6 bg-danger text-white p-5 d-flex justify-content-between rounded">
                            <div>Failed Orders</div>
                            <div>{stat?.failed ?? 0}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </PermissionGate>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default OngoingOrders;
